import React, { useState } from 'react';
import { ChevronLeft, Filter } from 'lucide-react';
import { motion } from 'framer-motion';
import { founders, sectors } from '../data/founders';
import { FounderCard } from './FounderCard';
import { InvestorAgenda } from './InvestorAgenda';
interface InvestorDashboardProps {
  onNavigate: () => void;
}
export function InvestorDashboard({
  onNavigate
}: InvestorDashboardProps) {
  const [activeSector, setActiveSector] = useState('All');
  const filteredFounders = founders.filter(f => activeSector === 'All' ? true : f.sector === activeSector);
  return <div className="min-h-screen flex flex-col w-full relative z-10 pb-20" data-id="element-182">
      {/* Hero Header */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1.2,
      ease: 'easeOut'
    }} className="relative w-full h-[280px] md:h-[360px] lg:h-[420px] overflow-hidden" data-id="element-183">
        {/* Two images side by side */}
        <div className="absolute inset-0 grid grid-cols-2" data-id="element-184">
          <motion.div initial={{
          opacity: 0,
          scale: 1.05
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1.5,
          ease: 'easeOut'
        }} className="relative overflow-hidden" data-id="element-185">
            <img src="https://cdn.magicpatterns.com/uploads/dxSd1AUP7W17F4S1mXVF8g/pexels-rdne-7491549.jpg" alt="Women in conversation" className="w-full h-full object-cover" data-id="element-186" />
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          scale: 1.05
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1.5,
          delay: 0.2,
          ease: 'easeOut'
        }} className="relative overflow-hidden" data-id="element-187">
            <img src="https://cdn.magicpatterns.com/uploads/jpq5Cszm2faKgKXSGECdij/pexels-rdne-7490981.jpg" alt="Women in meeting" className="w-full h-full object-cover" data-id="element-188" />
          </motion.div>
        </div>

        {/* Gradient overlays for fade effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e27]/60 via-[#0a0e27]/40 to-[#0a0e27] z-10" data-id="element-189" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0e27]/50 via-transparent to-[#0a0e27]/50 z-10" data-id="element-190" />
        {/* Seam blend in the middle */}
        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-24 md:w-32 bg-gradient-to-r from-transparent via-[#0a0e27]/40 to-transparent z-10" data-id="element-191" />
      </motion.div>

      {/* Dashboard content container */}
      <div className="w-full max-w-lg md:max-w-3xl lg:max-w-6xl mx-auto -mt-16 relative z-20" data-id="element-192">
        {/* Sticky Header */}
        <header className="sticky top-0 z-50 glass-panel px-4 md:px-8 py-4 md:py-6 mb-6 md:mb-8" data-id="element-193">
          <div className="flex items-center mb-4 md:mb-6" data-id="element-194">
            <button onClick={onNavigate} className="p-2 -ml-2 mr-2 md:mr-4 rounded-full hover:bg-white/10 transition-colors text-white/80" aria-label="Back to home" data-id="element-195">
              <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" data-id="element-196" />
            </button>
            <div data-id="element-197">
              <h1 className="font-sora font-bold text-xl md:text-2xl lg:text-3xl text-white" data-id="element-198">
                Investor Dashboard
              </h1>
              <p className="font-inter text-xs md:text-sm text-accent-cyan mt-1" data-id="element-199">
                Track & capture interest in real time
              </p>
            </div>
          </div>

          {/* Sector Filters */}
          <div className="flex items-center space-x-2 md:space-x-3 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 md:-mx-8 md:px-8" data-id="element-200">
            <div className="flex-shrink-0 p-1.5 md:p-2 rounded-full bg-white/5 border border-white/10 text-white/50 mr-1 md:mr-2" data-id="element-201">
              <Filter className="w-4 h-4 md:w-5 md:h-5" data-id="element-202" />
            </div>
            {sectors.map(sector => <button key={sector} onClick={() => setActiveSector(sector)} className={`flex-shrink-0 px-4 md:px-6 py-1.5 md:py-2 rounded-full text-sm md:text-base font-medium transition-all whitespace-nowrap ${activeSector === sector ? 'bg-gradient-to-r from-accent-cyan to-blue-600 text-white shadow-[0_0_10px_rgba(79,195,247,0.4)] border border-transparent' : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 hover:text-white'}`} data-id="element-203">
                {sector}
              </button>)}
          </div>
        </header>

        {/* Main Content */}
        <main className="px-4 md:px-8 flex-1" data-id="element-204">
          <InvestorAgenda data-id="element-205" />

          <div className="flex justify-between items-end mb-4 md:mb-6 px-1" data-id="element-206">
            <h2 className="font-sora font-semibold text-lg md:text-xl text-white/90" data-id="element-207">
              Founders
            </h2>
            <span className="text-xs md:text-sm font-inter text-white/50" data-id="element-208">
              {filteredFounders.length} results
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-209">
            {filteredFounders.map((founder, index) => <motion.div key={founder.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.05
          }} data-id="element-210">
                <FounderCard founder={founder} data-id="element-211" />
              </motion.div>)}

            {filteredFounders.length === 0 && <div className="col-span-full text-center py-12 md:py-20 glass-card rounded-2xl" data-id="element-212">
                <p className="text-white/50 font-inter md:text-lg" data-id="element-213">
                  No founders found in this sector.
                </p>
              </div>}
          </div>
        </main>
      </div>
    </div>;
}